import { SourSocketClient } from '@wayste/sour-socket';
import { apm } from '@elastic/apm-rum';
import axios, { isAxiosError } from 'axios';
import posthog from 'posthog-js';
import { AppAdminService } from '../services/AppAdmin/app-admin.service';
import AuditLogService from '../services/AuditLog/AuditLog.service';
import { AuthService } from '../services/Auth/auth.service';
import CRMService from '../services/CRM/CRM.service';
import CommercialService from '../services/Commercial/Commercial.service';
import CustomerService from '../services/Customer/Customer.service';
import DisposalSiteService from '../services/DisposalSite/DisposalSite.service';
import EquipmentService from '../services/Equipment/Equipment.service';
import HaulerService from '../services/Hauler/Hauler.service';
import InternalTicketService from '../services/InternalTicket/InternalTicket.service';
import InvoiceService from '../services/Invoice/Invoice.service';
import NotificationService from '../services/Notification/Notification.service';
import OperationsService from '../services/Operations/Operations.service';
import OrderService from '../services/Order/Order.service';
import PricingService from '../services/Pricing/Pricing.service';
import ProfileService from '../services/Profile/Profile.service';
import QuickBooksService from '../services/QuickBooks/QuickBooks.service';
import { StripeService as Stripe2Service } from '../services/Stripe/Stripe.service';
import { WaysteStripeService } from '../services/Stripe/WaysteStripe.service';
import TimeSheetService from '../services/TimeSheet/TimeSheet.service';
import UniversalService from '../services/UniversalService/Universal.service';
import Upload from '../services/Upload/Upload.service';
import UserService from '../services/User/User.service';
import VendorService from '../services/Vendor/Vendor.service';
// Unique client ID for this browser session - used to identify WebSocket messages from this client
const CLIENT_ID = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `client-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export class WaysteClient {
    static AppAdminService;
    static AuditLogService;
    static AuthService;
    static CommercialService;
    static Customer;
    static DisposalSiteService;
    static EquipmentService;
    static Hauler;
    static InternalTicketService;
    static InvoiceService;
    static NotificationService;
    static Order;
    static PricingService;
    static ProfileService;
    static QuickBooksService;
    static stripe;
    static TimeSheetService;
    static UniversalService;
    static Upload;
    static UserService;
    static VendorService;
    static WaysteStripeService;
    static OperationsService;
    static CRMService;
    static axios;
    options;
    constructor(options) {
        this.options = options;
        WaysteClient.axios = {
            mainInstance: axios.create({
                baseURL: options.API.baseApiUrl,
            }),
            appAdminInstance: axios.create({
                baseURL: options.API.baseApiUrl,
            }),
            socket: new SourSocketClient(options.API.baseApiUrl),
        };
        // Add X-Client-Id header to all requests for WebSocket message filtering
        WaysteClient.axios.mainInstance.interceptors.request.use((config) => {
            config.headers['X-Client-Id'] = CLIENT_ID;
            return config;
        });
        WaysteClient.axios.mainInstance.interceptors.response.use((response) => response, (error) => {
            posthog.captureException(error);
            console.error(error);
            if (error && isAxiosError(error)) {
                if (error.response?.data?.code === '23505') {
                    error.response.data.additionalInfo = 'Duplicate entry';
                }
            }
            if (apm.isActive()) {
                apm.captureError(error);
            }
            throw error;
        });
        this.setupServices();
    }
    // Should only run after auth is setup
    setupServices() {
        WaysteClient.AuthService = new AuthService(WaysteClient.axios, this.options.idTokenProvider, this.options);
        WaysteClient.UserService = new UserService(WaysteClient.AuthService, this.options);
        WaysteClient.ProfileService = new ProfileService(WaysteClient.AuthService, WaysteClient.axios, this.options);
        WaysteClient.InvoiceService = new InvoiceService(WaysteClient.axios);
        WaysteClient.Hauler = new HaulerService(WaysteClient.axios);
        if (this.options) {
            WaysteClient.OperationsService = new OperationsService(WaysteClient.axios);
            WaysteClient.CRMService = new CRMService(WaysteClient.axios);
            WaysteClient.stripe = new Stripe2Service(WaysteClient.axios);
            WaysteClient.VendorService = new VendorService(WaysteClient.AuthService, WaysteClient.axios, this.options);
            WaysteClient.AppAdminService = new AppAdminService(WaysteClient.AuthService, this.options);
            WaysteClient.AuditLogService = new AuditLogService(this.auth(), this.options);
            WaysteClient.CommercialService = new CommercialService(WaysteClient.axios);
            WaysteClient.Customer = new CustomerService(WaysteClient.axios);
            WaysteClient.EquipmentService = new EquipmentService(WaysteClient.axios);
            WaysteClient.Order = new OrderService(WaysteClient.axios);
            WaysteClient.PricingService = new PricingService(WaysteClient.axios);
            WaysteClient.QuickBooksService = new QuickBooksService(WaysteClient.axios);
            WaysteClient.TimeSheetService = new TimeSheetService(this.auth(), this.options);
            WaysteClient.UniversalService = new UniversalService(WaysteClient.axios);
            WaysteClient.Upload = new Upload(WaysteClient.AuthService, WaysteClient.axios, this.options);
            WaysteClient.WaysteStripeService = new WaysteStripeService(WaysteClient.AuthService, this.options);
        }
        WaysteClient.DisposalSiteService = new DisposalSiteService(WaysteClient.axios);
        WaysteClient.InternalTicketService = new InternalTicketService(WaysteClient.axios);
        WaysteClient.NotificationService = new NotificationService(WaysteClient.axios);
    }
    order() {
        return WaysteClient.Order;
    }
    hauler() {
        return WaysteClient.Hauler;
    }
    invoice() {
        return WaysteClient.InvoiceService;
    }
    customer() {
        return WaysteClient.Customer;
    }
    user() {
        return WaysteClient.UserService;
    }
    commercial() {
        return WaysteClient.CommercialService;
    }
    equipment() {
        return WaysteClient.EquipmentService;
    }
    profile() {
        return WaysteClient.ProfileService;
    }
    disposalSite() {
        return WaysteClient.DisposalSiteService;
    }
    auth() {
        return WaysteClient.AuthService;
    }
    internalTicket() {
        return WaysteClient.InternalTicketService;
    }
    notification() {
        return WaysteClient.NotificationService;
    }
    stripe() {
        return WaysteClient.stripe;
    }
    waysteStripe() {
        return WaysteClient.WaysteStripeService;
    }
    vendorService() {
        return WaysteClient.VendorService;
    }
    appAdmin() {
        return WaysteClient.AppAdminService;
    }
    timeSheet() {
        return WaysteClient.TimeSheetService;
    }
    quickBooks() {
        return WaysteClient.QuickBooksService;
    }
    universalService() {
        return WaysteClient.UniversalService;
    }
    upload() {
        return WaysteClient.Upload;
    }
    pricing() {
        return WaysteClient.PricingService;
    }
    auditLog() {
        return WaysteClient.AuditLogService;
    }
    operations() {
        return WaysteClient.OperationsService;
    }
    crm() {
        return WaysteClient.CRMService;
    }
    /**
     * Get the unique client ID for this browser session.
     * Used to identify WebSocket messages that originated from this client.
     */
    getClientId() {
        return CLIENT_ID;
    }
}
//# sourceMappingURL=WaysteClient.js.map